import React, { useState } from 'react';
import Cookies from 'js-cookie';
import firebase from '../../Firebase';
import { useAuthContext } from '../../hooks/useAuthContext';
import DobInput from '../DobInput';
import NearestLocationForm from '../Location/NearestLocationField';
import ProfilePicturesGrid from '../Uploads/ProfilePhotoUpload';
import DynamicFormMain from '../Profile&Settings/CommunityProfile/DynamicFormMain';
import { QUICK_REPLY_EVENT } from './Message';
import './ChatProfileWidget.css';

// The community bot's profile card: the fields for everything the member's
// profile is missing, together in one message — the Profile page's own
// first name, date of birth, location, photos and community questions,
// saving as they go. Sent as a bot message whose text is
// PROFILE_WIDGET_PREFIX + {"sections": [...]}; the prefix must match
// backend/services/botProfileFlow.js. Done ✓ asks the bot to check again.
export const PROFILE_WIDGET_PREFIX = '__PROFILE_WIDGET__';

// Saves first name / date of birth on their main profile (as Profile → Basic Info).
const saveBasics = async (userId, UserBasics) => {
  const idToken = await firebase.auth().currentUser.getIdToken();
  const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/userProfile/updateProfile`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}`, 'X-CSRF-Token': Cookies.get('mytoken') },
    body: JSON.stringify({ userId, UserBasics }),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
};

const NameField = ({ userId }) => {
  const [name, setName] = useState('');
  const [state, setState] = useState('');
  const save = async (e) => {
    e.preventDefault();
    const first = name.trim().split(/\s+/)[0] || '';
    if (!first) return;
    setState('Saving…');
    try {
      await saveBasics(userId, { firstName: first.charAt(0).toUpperCase() + first.slice(1) });
      setState('Saved ✓');
    } catch {
      setState("Couldn't save — try again");
    }
  };
  return (
    <form className="chatProfile-row" onSubmit={save}>
      <input type="text" value={name} onChange={(e) => { setName(e.target.value); setState(''); }} placeholder="Your first name" aria-label="Your first name" maxLength={40} autoComplete="given-name" />
      <button type="submit" disabled={!name.trim()}>Save</button>
      {state && <span className="chatProfile-status">{state}</span>}
    </form>
  );
};

const DobField = ({ userId }) => {
  const [dob, setDob] = useState('');
  const [state, setState] = useState('');
  const onChange = async (iso) => {
    setDob(iso);
    const d = new Date(`${iso}T12:00:00Z`);
    const age = Math.floor((Date.now() - d.getTime()) / (365.25 * 24 * 3600 * 1000));
    if (age < 18) { setState('You need to be 18 or over to join.'); return; }
    setState('Saving…');
    try {
      await saveBasics(userId, { dateOfBirth: d });
      setState('Saved ✓');
    } catch {
      setState("Couldn't save — try again");
    }
  };
  return (
    <div className="chatProfile-row">
      <DobInput id="chatDob" value={dob} onChange={onChange} />
      {state && <span className="chatProfile-status">{state}</span>}
    </div>
  );
};

const SECTIONS = {
  name: 'First name',
  dob: 'Date of birth',
  location: "Where you're based",
  photos: 'A photo of yourself',
  community: 'Your answers',
};

const ChatProfileWidget = ({ communityId, sections = [] }) => {
  const { user } = useAuthContext();
  const [done, setDone] = useState(false);
  if (!user?.userId) return null;
  const known = sections.filter(s => SECTIONS[s]);
  return (
    <div className="chatProfile">
      {known.map((s) => (
        <section key={s} className="chatProfile-section">
          <h4>{SECTIONS[s]}</h4>
          {s === 'name' && <NameField userId={user.userId} />}
          {s === 'dob' && <DobField userId={user.userId} />}
          {s === 'location' && <NearestLocationForm />}
          {s === 'photos' && <ProfilePicturesGrid setPhotosInfo={() => {}} />}
          {s === 'community' && (
            <DynamicFormMain
              user={user}
              communityId={communityId}
              mode="Profile"
              autoSaveOnBlur
              onCommunityName={() => {}}
              setCommunityProfileComplete={() => {}}
            />
          )}
        </section>
      ))}
      <button
        type="button"
        className="chatProfile-done"
        disabled={done}
        onClick={() => { setDone(true); window.dispatchEvent(new CustomEvent(QUICK_REPLY_EVENT, { detail: { text: 'Done ✓' } })); setTimeout(() => setDone(false), 4000); }}
      >
        Done ✓
      </button>
    </div>
  );
};

export default ChatProfileWidget;
